/* eslint-disable no-negated-condition */
/* eslint-disable babel/no-unused-expressions */
/* eslint-disable @typescript-eslint/no-unused-expressions */
/* eslint-disable react/default-props-match-prop-types */

import React, { useState, useEffect, useRef } from "react";
import {
  Breadcrumb,
  BreadcrumbProps,
  Breadcrumbs,
  Heading,
  Hidden as HiddenBase,
  Section,
  Stack,
} from "@origin-digital/ods-core";

import styled from "styled-components";
import {
  HeadingComponentVariants,
  ComponentBaseProps,
} from "@origin-digital/ods-types";
import { ActionBarMobile } from "./ActionBarMobile";
import { NavTitle } from "./NavTitle";
import { FullSelect } from "./PropertySelect/PropertySelect";
import { AddressLine } from "./AddressLine/AddressLine";
import { isEmptyPremise, hasSelectedPremise, isSinglePremise } from "./utils";
import { PropertyPopper } from "./PropertyPopper/PropertyPopper";
import { Box } from "./Box";

export interface Premise {
  premiseName: string;
  premiseId: string;
}

export type MobileNavButtonIconType = "back" | "close";
export type MobileNavButtonType = "none" | MobileNavButtonIconType;
export type MobilePropertySelectorType =
  | "none"
  | "compact"
  | "full"
  | "readOnly";

export interface Breadcrumbs
  extends Pick<BreadcrumbProps, "active" | "href" | "onClick"> {
  label: string;
}

export interface ActionBarProps extends ComponentBaseProps {
  title?: string;
  premises?: Premise[];
  headingComponent?: HeadingComponentVariants;
  selectedPremiseId?: string;
  breadcrumbs?: Breadcrumbs[];
  mobileNavButton?: MobileNavButtonType;
  onClickMobileNavButton?: (
    event?: React.MouseEvent<HTMLButtonElement>
  ) => void | boolean;
  onSelectPremise?: (premiseId: string) => void;
  mobilePropertySelector?: MobilePropertySelectorType;
  desktopVariant?: "default" | "slim";
}

const slimHeight = 44;
const fullHeight = 128;

const ViewContainer = styled(Box)<{
  desktopVariant: ActionBarProps["desktopVariant"];
}>`
  @media (min-width: ${(p) => p.theme.breakpoints.lg}) {
    justify-content: space-between;
    height: ${(p) => (p.desktopVariant === "slim" ? slimHeight : fullHeight)}px;
  }
`;

const SELECT_LABEL_OFFSET_PX = 54;

const DesktopPremiseSelectContainer = styled.div<{ width: number }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${(props) => props.width + SELECT_LABEL_OFFSET_PX}px;
`;

const StyledHidden = styled(HiddenBase)`
  width: 100%;
`;

export const ActionBar: React.FC<ActionBarProps> = ({
  title,
  headingComponent,
  premises,
  mobilePropertySelector,
  selectedPremiseId,
  breadcrumbs,
  mobileNavButton,
  onSelectPremise,
  onClickMobileNavButton,
  "data-id": dataId,
  desktopVariant,
}) => {
  const [selectedAddress, setSelectedAddress] = useState("");
  const [selectedLabelWidthPx, setSelectedLabelWidthPx] = useState(0);

  const hiddenRef = useRef<HTMLDivElement>(null);

  const calcSelectWidth = () => {
    const { current } = hiddenRef;
    if (current) {
      if (current.offsetWidth !== selectedLabelWidthPx) {
        setSelectedLabelWidthPx(current.offsetWidth);
      }
    }
  };

  const getSelectedPremise = () => {
    return premises?.find((premise) => premise.premiseId === selectedPremiseId);
  };

  const handleOnSelectPremise = (premiseId: string) => {
    const selectedProperty = getSelectedPremise();

    // If a different address is selected, render the new label to get width
    if (selectedAddress !== selectedProperty?.premiseName) {
      setSelectedAddress(selectedProperty?.premiseName || "");
    }
    onSelectPremise && onSelectPremise(premiseId);
  };

  const getSelectProps = () => {
    const premiseOptions = premises?.map((premise) => ({
      label: premise.premiseName,
      value: premise.premiseId,
    }));

    return {
      options: premiseOptions || [],
      selectedValue: selectedPremiseId,
      onChange: (event: any) => handleOnSelectPremise(event.target.value),
    };
  };

  // ComponentDidMount
  useEffect(() => {
    // code to run on component mount
    // If documents.fonts is supported, re-render select width when fonts have loaded
    // Document.fonts not currently supported by typescript
    if ((document as any)["fonts"]) {
      (document as any)["fonts"].ready.then(() => {
        calcSelectWidth();
      });
    }
    // Calculate select width using default browser font
    calcSelectWidth();
  }, []);

  // ComponentDidUpdate
  useEffect(() => {
    const selectedPremise = getSelectedPremise();
    if (selectedPremise && selectedPremise.premiseName !== selectedAddress) {
      setSelectedAddress(selectedPremise.premiseName);
    }
    // Re-calculate size after the hidden label has rendered
    calcSelectWidth();
  });

  const [boxRef, setBoxRef] = React.useState<HTMLElement | null>(null);

  return (
    <Box overflow="hidden">
      <div
        data-id="hidden-address-label"
        style={{
          fontSize: "12px",
          position: "fixed",
          top: 0,
          left: 0,
          visibility: "hidden",
        }}
        ref={hiddenRef}
      >
        {selectedAddress}
      </div>
      <Box
        data-id={dataId}
        backgroundColor="white"
        display="flex"
        flexDirection="column"
        style={{ flex: 1 }}
      >
        <ViewContainer
          desktopVariant={desktopVariant}
          display="flex"
          backgroundColor="white"
          width="full"
        >
          <StyledHidden below="lg">
            <Box width="full" display="flex" height="full" alignItems="center">
              {desktopVariant !== "slim" ? (
                <Section paddingY="none" data-id="desktop-container">
                  <Stack space="medium">
                    <Box
                      width="full"
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Heading
                        variant="h2"
                        data-id="actionbar-heading-desktop"
                        component={headingComponent}
                      >
                        {title}
                      </Heading>
                      <Box innerRef={setBoxRef}>
                        {mobilePropertySelector !== "none" &&
                        !isEmptyPremise(premises) &&
                        hasSelectedPremise(premises, selectedPremiseId) ? (
                          <DesktopPremiseSelectContainer
                            width={selectedLabelWidthPx}
                            data-id="actionbar-address-select-desktop-container"
                          >
                            {mobilePropertySelector === "readOnly" ||
                            isSinglePremise(premises) ? (
                              <AddressLine
                                address={
                                  getSelectedPremise()?.premiseName || ""
                                }
                              />
                            ) : (
                              <>
                                <FullSelect
                                  data-id="actionbar-address-select-full-desktop"
                                  width={selectedLabelWidthPx}
                                  {...getSelectProps()}
                                />
                                <PropertyPopper
                                  id="desktop-popper"
                                  anchorEl={boxRef}
                                />
                              </>
                            )}
                          </DesktopPremiseSelectContainer>
                        ) : (
                          <Stack>
                            <NavTitle
                              mobilePropertySelector={mobilePropertySelector}
                              premises={premises}
                              selectedPremiseId={selectedPremiseId}
                              handleOnSelectPremise={handleOnSelectPremise}
                              mobileNavButton={mobileNavButton}
                              headingComponent={headingComponent}
                              onClickMobileNavButton={onClickMobileNavButton}
                              title={title}
                              desktopVariant={desktopVariant}
                              isDesktop={true}
                            />
                          </Stack>
                        )}
                      </Box>
                    </Box>
                    {breadcrumbs?.length ? (
                      <Breadcrumbs>
                        {breadcrumbs.map(({ label, ...breadcrumbProps }) => (
                          <Breadcrumb key={label} {...breadcrumbProps}>
                            {label}
                          </Breadcrumb>
                        ))}
                      </Breadcrumbs>
                    ) : null}
                  </Stack>
                </Section>
              ) : (
                <Stack>
                  <NavTitle
                    mobilePropertySelector={mobilePropertySelector}
                    premises={premises}
                    selectedPremiseId={selectedPremiseId}
                    handleOnSelectPremise={handleOnSelectPremise}
                    mobileNavButton={mobileNavButton}
                    headingComponent={headingComponent}
                    onClickMobileNavButton={onClickMobileNavButton}
                    title={title}
                    desktopVariant={desktopVariant}
                    isDesktop={true}
                  />
                </Stack>
              )}
            </Box>
          </StyledHidden>
          <StyledHidden above="md">
            <ActionBarMobile
              title={title}
              headingComponent={headingComponent}
              mobilePropertySelector={mobilePropertySelector}
              mobileNavButton={mobileNavButton}
              premises={premises}
              selectedPremiseId={selectedPremiseId}
              handleOnSelectPremise={handleOnSelectPremise}
              onClickMobileNavButton={onClickMobileNavButton}
            />
          </StyledHidden>
        </ViewContainer>
      </Box>
    </Box>
  );
};

ActionBar.displayName = "ActionBar";
ActionBar.defaultProps = {
  mobileNavButton: "none",
  mobilePropertySelector: "compact",
  premises: [],
  headingComponent: "h1",
  "data-id": "action-bar",
  desktopVariant: "default",
};
